import { InlineError } from "@/components/QueryState";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type BooMemberCandidate, type BooPermissions } from "@/lib/api";
import { booRoleOfLevel, type DashboardLevel, dashboardLevels, levelLabel } from "@/lib/people-levels";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { initials } from "./person-format";
import { LevelRadioGroup } from "./PersonParts";
import { peopleKey } from "./use-identity";

const candidatesKey = (containerId: string) => ["boo-member-candidates", containerId];

function candidateName(p: BooMemberCandidate): string {
  return p.name || p.email || "Someone";
}

// Picks org members who aren't in this Boo yet and gives them one level.
// Each person is added through the members route, so the roster push tells
// the Boo about them the same way an accepted invite does.
export function AddFromOrgDialog({
  containerId,
  perms,
  open,
  onClose,
}: {
  containerId: string;
  perms: Partial<BooPermissions> | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const candidates = useQuery({
    queryKey: candidatesKey(containerId),
    queryFn: () => api.containers.members.candidates(containerId),
    enabled: open && !!containerId,
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [level, setLevel] = useState<DashboardLevel>("standard");
  const levels = dashboardLevels(perms);
  const people = candidates.data?.data ?? [];
  const chosen = people.filter((p) => picked.includes(p.userId));

  const close = () => {
    setPicked([]);
    setLevel("standard");
    onClose();
  };

  const add = useMutation({
    mutationFn: async () => {
      const failed: Array<{ person: BooMemberCandidate; error: unknown }> = [];
      let added = 0;
      for (const person of chosen) {
        try {
          await api.containers.members.add(containerId, person.userId, booRoleOfLevel(level));
          added += 1;
        } catch (error) {
          failed.push({ person, error });
        }
      }
      return { added, failed };
    },
    onSuccess: ({ added, failed }) => {
      queryClient.invalidateQueries({ queryKey: peopleKey(containerId) });
      queryClient.invalidateQueries({ queryKey: candidatesKey(containerId) });
      queryClient.invalidateQueries({ queryKey: ["boo-members", containerId] });
      queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] });
      if (failed.length === 0) {
        toast.success(
          added === 1
            ? `${candidateName(chosen[0])} is now ${levelLabel(level)} in this Boo.`
            : `${added} people are now ${levelLabel(level)} in this Boo.`,
        );
        close();
        return;
      }
      const names = failed.map((f) => candidateName(f.person)).join(", ");
      const reason = failed[0].error instanceof Error && failed[0].error.message ? ` ${failed[0].error.message}` : "";
      toast.error(added > 0 ? `${added} added. Couldn't add ${names}.${reason}` : `Couldn't add ${names}.${reason}`);
      setPicked(failed.map((f) => f.person.userId));
    },
  });

  const toggle = (userId: string) =>
    setPicked((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !add.isPending && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add from organization</DialogTitle>
          <DialogDescription>
            People in your organization who already have a dashboard account. They can open this Boo as soon as you add them.
          </DialogDescription>
        </DialogHeader>
        {candidates.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : candidates.isError ? (
          <InlineError error={candidates.error} />
        ) : people.length === 0 ? (
          <p className="text-sm text-muted-foreground text-pretty">
            Everyone in your organization is already in this Boo. To bring in someone new, invite them to the dashboard.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <Command
              className="rounded-lg border"
              filter={(value, query) => (value.toLowerCase().includes(query.trim().toLowerCase()) ? 1 : 0)}
            >
              <CommandInput placeholder="Search by name or email" />
              <CommandList className="max-h-64">
                <CommandEmpty>No one matches.</CommandEmpty>
                <CommandGroup>
                  {people.map((p) => {
                    const checked = picked.includes(p.userId);
                    const name = candidateName(p);
                    return (
                      <CommandItem
                        key={p.userId}
                        value={`${p.name ?? ""} ${p.email ?? ""}`}
                        onSelect={() => toggle(p.userId)}
                        className="gap-3"
                      >
                        <Checkbox checked={checked} tabIndex={-1} aria-label={`Add ${name}`} className="pointer-events-none" />
                        <Avatar className="size-7 shrink-0">
                          <AvatarFallback className="text-[10px]">{initials(p.name, p.email ?? "?")}</AvatarFallback>
                        </Avatar>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm">{name}</span>
                          {p.name && p.email && <span className="truncate text-xs text-muted-foreground">{p.email}</span>}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-medium">Level</legend>
              <LevelRadioGroup value={level} options={levels} onChange={setLevel} />
              {!perms?.canManageAdmins && (
                <p className="text-xs text-muted-foreground">Only this Boo&apos;s owners can add someone as Admin or Owner.</p>
              )}
            </fieldset>
          </div>
        )}
        <DialogFooter className="sm:items-center">
          {chosen.length > 0 && (
            <span className="mr-auto text-xs text-muted-foreground tabular-nums">
              {chosen.length === 1 ? candidateName(chosen[0]) : `${chosen.length} people`} as {levelLabel(level)}
            </span>
          )}
          <Button variant="outline" onClick={close} disabled={add.isPending}>
            Cancel
          </Button>
          <Button disabled={chosen.length === 0 || add.isPending} onClick={() => add.mutate()}>
            {add.isPending && <Loader2 className="size-3.5 animate-spin" />}
            {chosen.length > 1 ? `Add ${chosen.length} people` : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
